const MINIMUM_MAJOR_UNITS = 1;

export function apiErrorMessage(error, fallback) {
  const body = error?.response?.data;
  return body?.errorMessage || body?.message || body?.error || fallback;
}

export function toMinorUnits(value) {
  const amount = Number(value);
  if (value === "" || !Number.isFinite(amount) || amount < MINIMUM_MAJOR_UNITS) return null;
  return Math.round(amount * 100);
}

export function formatMoney(cents, currency) {
  if (cents == null) return "-";
  const major = cents / 100;
  if (/^[A-Za-z]{3}$/.test(currency || "")) {
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(major);
  }
  return major.toFixed(2);
}

export function formatCount(value) {
  return value == null ? "-" : Number(value).toLocaleString("en-AU");
}

export function clickThroughRate(clicks, impressions) {
  if (clicks == null || !impressions) return "-";
  return `${((clicks / impressions) * 100).toFixed(2)}%`;
}

export function maxSpendCents(dailyBudgetCents, days) {
  return days > 0 ? dailyBudgetCents * days : 0;
}

export function parseCountries(text) {
  return (text || "")
    .split(/[\s,]+/)
    .map((code) => code.trim().toUpperCase())
    .filter(Boolean);
}

const TONES = {
  running: "bg-app-status-completed-bg text-app-status-completed-text",
  review: "bg-app-status-in-progress-bg text-app-status-in-progress-text",
  attention: "bg-app-status-cancelled-bg text-app-status-cancelled-text",
  idle: "bg-app-status-hold-bg text-app-status-hold-text",
  neutral: "bg-app-status-pending-bg text-app-status-pending-text",
};

const REVIEW = ["PENDING_REVIEW", "IN_PROCESS", "PENDING_BILLING_INFO"];
const ATTENTION = ["DISAPPROVED", "WITH_ISSUES"];

/** What the person should read from the campaign: our start/pause flag combined with Meta's own state. */
export function campaignState(campaign) {
  const { status, effectiveStatus } = campaign;
  if (status === "ARCHIVED" || status === "DELETED") {
    return { label: status === "DELETED" ? "Removed" : "Archived", tone: TONES.neutral };
  }
  if (status !== "ACTIVE") return { label: "Paused", tone: TONES.idle };
  if (!effectiveStatus || effectiveStatus === "ACTIVE") {
    return { label: "Running", tone: TONES.running };
  }
  if (REVIEW.includes(effectiveStatus)) return { label: "In review", tone: TONES.review };
  if (ATTENTION.includes(effectiveStatus))
    return { label: "Needs attention", tone: TONES.attention };
  return { label: "Not delivering", tone: TONES.idle };
}

export function summarize(campaigns) {
  return campaigns.reduce(
    (totals, campaign) => ({
      running: totals.running + (campaignState(campaign).label === "Running" ? 1 : 0),
      spendCents: totals.spendCents + (campaign.spendCents || 0),
      impressions: totals.impressions + (campaign.impressions || 0),
      clicks: totals.clicks + (campaign.clicks || 0),
      results: totals.results + (campaign.results || 0),
    }),
    { running: 0, spendCents: 0, impressions: 0, clicks: 0, results: 0 }
  );
}

export const OBJECTIVES = [
  { value: "OUTCOME_AWARENESS", label: "Awareness (reach people)" },
  { value: "OUTCOME_TRAFFIC", label: "Traffic (visits to a link)" },
  { value: "OUTCOME_ENGAGEMENT", label: "Engagement (likes, comments, messages)" },
  { value: "OUTCOME_LEADS", label: "Leads (enquiries)" },
];

export const EFFECTIVE_STATUS_HELP = {
  PENDING_REVIEW: "Meta is reviewing the ad. It starts delivering once approved.",
  IN_PROCESS: "Meta is still processing the ad.",
  DISAPPROVED: "Meta rejected the ad. Open it in Ads Manager to see why.",
  WITH_ISSUES: "Meta reports a problem with the ad. Open it in Ads Manager.",
  ADSET_PAUSED: "The campaign is on, but its ad set is paused in Meta.",
  CAMPAIGN_PAUSED: "The campaign is paused.",
};

export function currencyOf(connections, connectionId) {
  return connections.find((connection) => connection.id === connectionId)?.currency || null;
}

export function labelOf(connections, connectionId) {
  return connections.find((connection) => connection.id === connectionId)?.label || "";
}

/** Money from accounts billed in different currencies cannot be added up. */
export function totalsCurrency(connections, campaigns) {
  const currencies = new Set(campaigns.map((c) => currencyOf(connections, c.connectionId)));
  if (currencies.size > 1) return { mixed: true, currency: null };
  return { mixed: false, currency: [...currencies][0] || null };
}

export const AD_GOALS = [
  { value: "WEBSITE", label: "Website visits" },
  { value: "LEADS", label: "Leads" },
  { value: "MESSAGES", label: "Messages" },
  { value: "REACH", label: "Reach" },
];

export const AD_GENDERS = [
  { value: "ALL", label: "Everyone" },
  { value: "FEMALE", label: "Women" },
  { value: "MALE", label: "Men" },
];

export const AD_BUTTONS = [
  { value: "LEARN_MORE", label: "Learn more" },
  { value: "SIGN_UP", label: "Sign up" },
  { value: "CONTACT_US", label: "Contact us" },
  { value: "APPLY_NOW", label: "Apply now" },
  { value: "GET_QUOTE", label: "Get quote" },
  { value: "SUBSCRIBE", label: "Subscribe" },
  { value: "NO_BUTTON", label: "No button" },
];

const LEAD_BUTTON_VALUES = ["SIGN_UP", "APPLY_NOW", "GET_QUOTE", "LEARN_MORE", "SUBSCRIBE"];
export const LEAD_BUTTONS = AD_BUTTONS.filter((button) =>
  LEAD_BUTTON_VALUES.includes(button.value)
);

export const AD_IMAGE_TYPES = ["image/jpeg", "image/png"];
export const AD_IMAGE_MAX_BYTES = 8 * 1024 * 1024;

export function isWebLink(value) {
  return /^https?:\/\/[^\s/]+\.[^\s]+$/i.test((value || "").trim());
}
